import { useEffect, useRef } from 'react';

const commercialCards = [
  {
    icon: 'ri-shopping-basket-2-line',
    title: 'Pedido Mínimo',
    desc: 'Pedido mínimo de 10 unidades, podendo escolher sabores variados.',
    color: 'bg-accent-50 text-accent-600',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Validade',
    desc: 'Os sanduíches possuem validade de 15 dias, desde que armazenados corretamente.',
    color: 'bg-accent-50 text-accent-600',
  },
  {
    icon: 'ri-fridge-line',
    title: 'Conservação',
    desc: 'Manter refrigerado entre 0°C e 10°C para garantir frescor e segurança.',
    color: 'bg-accent-50 text-accent-600',
  },
];

const paymentMethods = [
  { icon: 'ri-bank-card-line', label: 'Pix' },
  { icon: 'ri-money-cny-circle-line', label: 'Dinheiro' },
  { icon: 'ri-exchange-dollar-line', label: 'Transferência' },
  { icon: 'ri-file-list-3-line', label: 'Boleto (com análise)' },
];

const regions = ['Fortaleza', 'Maracanaú', 'Caucaia', 'Eusébio'];

const CommercialInfo = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.querySelectorAll('.reveal').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="comercial"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-background-100"
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="reveal inline-flex items-center gap-2 font-label text-xs md:text-sm text-accent-600 tracking-widest uppercase mb-4">
              <span className="w-6 h-px bg-accent-400" />
              Informações Comerciais
              <span className="w-6 h-px bg-accent-400" />
            </span>
            <h2 className="reveal reveal-delay-1 font-display text-3xl md:text-5xl text-foreground-900 leading-tight">
              Tudo que você <span className="text-accent-600">precisa saber</span>
            </h2>
          </div>

          {/* Top row: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-8 md:mb-10">
            {commercialCards.map((card, index) => (
              <div
                key={card.title}
                className="reveal p-6 md:p-8 rounded-3xl bg-background-50 border border-background-200/60 hover:border-accent-200 hover:shadow-md transition-all duration-500"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className={`w-12 h-12 rounded-2xl ${card.color} flex items-center justify-center mb-4`}>
                  <i className={`${card.icon} text-xl`} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-2">
                  {card.title}
                </h3>
                <p className="font-body text-sm text-foreground-500 leading-relaxed">
                  {card.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom row: 2 cards (pagamento + regiões) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {/* Pagamento */}
            <div className="reveal p-6 md:p-8 rounded-3xl bg-background-50 border border-background-200/60">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-accent-50 flex items-center justify-center">
                  <i className="ri-secure-payment-line text-xl text-accent-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-800">
                  Formas de Pagamento
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {paymentMethods.map((method) => (
                  <div
                    key={method.label}
                    className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-background-100/50 border border-background-200/40 hover:border-accent-200 transition-all"
                  >
                    <i className={`${method.icon} text-xl text-accent-500`} />
                    <span className="font-label text-xs text-foreground-600 text-center">
                      {method.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Regiões + Entregas */}
            <div className="reveal p-6 md:p-8 rounded-3xl bg-background-50 border border-background-200/60">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-accent-50 flex items-center justify-center">
                  <i className="ri-map-pin-5-line text-xl text-accent-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-800">
                  Regiões Atendidas
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {regions.map((region) => (
                  <div
                    key={region}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent-50/50 border border-accent-100/50"
                  >
                    <i className="ri-map-pin-line text-xs text-accent-500" />
                    <span className="font-label text-sm text-foreground-700">
                      {region}
                    </span>
                  </div>
                ))}
              </div>
              <p className="font-body text-xs text-foreground-400 leading-relaxed">
                Entregas por rota sem taxa. Saídas às 07:00, entregas até às 17:00. Horários podem variar conforme trajeto.
              </p>
            </div>
          </div>

          {/* Política de Troca */}
          <div className="reveal mt-8 md:mt-10 p-6 md:p-8 rounded-3xl bg-gradient-to-r from-accent-50/60 to-accent-50/30 border border-accent-100/30">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                <i className="ri-refresh-line text-accent-600" />
              </div>
              <div>
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-800 mb-1">
                  Política de Troca
                </h3>
                <p className="font-body text-sm text-foreground-500 leading-relaxed">
                  Não realizamos trocas de produtos vencidos na posse do cliente ou que não foram vendidos por baixa rotatividade. Caso o produto apresente avaria no ato da entrega, a substituição será realizada imediatamente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommercialInfo;